import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Phone, Clock, Shield } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import StickyCallButton from '@/components/StickyCallButton';
import PageSEO from '@/components/PageSEO';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DistrictPage = () => {
  const { citySlug, districtSlug } = useParams();
  const [district, setDistrict] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDistrict = async () => {
      try {
        const response = await axios.get(`${API}/districts/${citySlug}/${districtSlug}`);
        setDistrict(response.data);
      } catch (error) {
        console.error('Fehler beim Laden des Stadtteils:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDistrict();
  }, [citySlug, districtSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center py-20">
          <p className="text-gray-500">Lade Daten...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!district) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex flex-col items-center justify-center py-20">
          <p className="text-gray-500 text-lg mb-4">Stadtteil nicht gefunden</p>
          <Link to="/einsatzgebiete" className="text-[#0066cc] hover:underline">Zurück zur Übersicht</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <PageSEO
        title={`Handwerkernotdienst ${district.city}-${district.name} | 24/7 Notdienst`}
        description={`24/7 Handwerkernotdienst in ${district.city}-${district.name}. Schlüsseldienst, Elektro, Sanitär & mehr. Schnell vor Ort ☎ 0231 39083687. Lokale Fachkräfte für ${district.name}.`}
        canonicalUrl={`https://handwerkernotdienst.nrw/einsatzgebiete/${district.citySlug}/stadtteil/${districtSlug}`}
        breadcrumbs={[
          { name: 'Start', url: 'https://handwerkernotdienst.nrw/' },
          { name: 'Einsatzgebiete', url: 'https://handwerkernotdienst.nrw/einsatzgebiete' },
          { name: district.city, url: `https://handwerkernotdienst.nrw/einsatzgebiete/${district.citySlug}` },
          { name: district.name, url: `https://handwerkernotdienst.nrw/einsatzgebiete/${district.citySlug}/stadtteil/${districtSlug}` }
        ]}
      />
      
      <Header />
      <StickyCallButton />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#e8f4f8] to-white py-16" data-testid="district-hero">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-[#0066cc]">Start</Link>
            <span>/</span>
            <Link to="/einsatzgebiete" className="hover:text-[#0066cc]">Einsatzgebiete</Link>
            <span>/</span>
            <Link to={`/einsatzgebiete/${district.citySlug}`} className="hover:text-[#0066cc]">{district.city}</Link>
            <span>/</span>
            <span className="text-gray-900">{district.name}</span>
          </div>

          <div className="flex items-start space-x-4 mb-6">
            <div className="w-16 h-16 bg-[#0066cc] rounded-xl flex items-center justify-center flex-shrink-0">
              <MapPin className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3" data-testid="district-title">
                Handwerkernotdienst in {district.city}-{district.name}
              </h1>
              <p className="text-lg text-gray-600">
                24/7 Notdienst direkt in Ihrem Stadtteil
              </p>
            </div>
          </div>

          <a href="tel:+4923139083687" data-testid="district-call-button">
            <Button size="lg" className="bg-[#e63946] hover:bg-[#d62828] text-white px-8 rounded-full font-bold">
              <Phone className="w-5 h-5 mr-2" />
              Jetzt anrufen: 0231 39083687
            </Button>
          </a>
        </div>
      </section>

      {/* Content */}
      <section className="py-16" data-testid="district-content">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Ihr Notdienst vor Ort in {district.city}-{district.name}
            </h2>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              {district.description} Der Stadtteil {district.name} ist ein wichtiger Bestandteil von {district.city}. 
              Unser Handwerkernotdienst kennt die Gegebenheiten vor Ort und ist schnellstmöglich bei Ihnen, wenn Sie uns brauchen.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              Ob Türöffnung nach dem Aussperren, Stromausfall, Wasserschaden oder andere Notfälle – 
              wir sind 24 Stunden am Tag, 7 Tage die Woche für Sie erreichbar. Unsere erfahrenen Techniker 
              kommen mit vollständig ausgerüsteten Fahrzeugen direkt zu Ihnen nach {district.name}.
            </p>

            <div className="grid md:grid-cols-3 gap-6 my-8">
              <div className="text-center p-6 bg-gray-50 rounded-xl">
                <Clock className="w-10 h-10 text-[#0066cc] mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">24/7 Erreichbar</h3>
                <p className="text-gray-600 text-sm">Rund um die Uhr für Sie da</p>
              </div>
              <div className="text-center p-6 bg-gray-50 rounded-xl">
                <MapPin className="w-10 h-10 text-[#0066cc] mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Lokale Nähe</h3>
                <p className="text-gray-600 text-sm">Kurze Wege in {district.name}</p>
              </div>
              <div className="text-center p-6 bg-gray-50 rounded-xl">
                <Shield className="w-10 h-10 text-[#0066cc] mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Professionell</h3>
                <p className="text-gray-600 text-sm">Qualifizierte Fachkräfte</p>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-4">Typische Notfälle in {district.name}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              In {district.name} werden wir regelmäßig zu folgenden Notfällen gerufen:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li><strong>Schlüsseldienst:</strong> Ausgesperrt aus der Wohnung oder dem Haus, Schlüssel verloren oder abgebrochen</li>
              <li><strong>Elektro-Notdienst:</strong> Stromausfall, defekte Sicherungen, elektrische Gefahren</li>
              <li><strong>Sanitär-Notdienst:</strong> Rohrbruch, Wasserschaden, verstopfte Leitungen</li>
              <li><strong>Heizungsausfall:</strong> Besonders im Winter ein dringender Notfall</li>
              <li><strong>Glas-Notdienst:</strong> Zerbrochene Fenster oder Türen nach Einbruch oder Unfall</li>
            </ul>

            {district.postal_codes && district.postal_codes.length > 0 && (
              <div className="bg-[#e8f4f8] p-6 rounded-xl my-8">
                <h4 className="font-semibold text-gray-900 mb-3">
                  Postleitzahlen in {district.name}
                </h4>
                <p className="text-gray-700 mb-3">
                  Wir sind in folgenden PLZ-Bereichen für Sie aktiv:
                </p>
                <div className="flex flex-wrap gap-2">
                  {district.postal_codes.map((plz, index) => (
                    <Link
                      key={index}
                      to={`/plz/${plz}`}
                      className="bg-white px-4 py-2 rounded-lg text-[#0066cc] font-semibold hover:bg-[#0066cc] hover:text-white transition-colors"
                      data-testid={`plz-link-${index}`}
                    >
                      {plz}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Unsere Einsatzzeiten</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Unser Notdienst ist 365 Tage im Jahr für Sie verfügbar – an Werktagen, Wochenenden und Feiertagen, 
              tagsüber und nachts. Wann immer Sie uns brauchen, wir sind für Sie da. Rufen Sie einfach unsere 
              24-Stunden-Hotline unter <a href="tel:+4923139083687" className="text-[#0066cc] font-semibold hover:underline">0231 39083687</a> an.
            </p>

            {/* Placeholder Image */}
            <div className="bg-gray-200 rounded-xl h-64 flex items-center justify-center my-8" data-testid="district-image-placeholder">
              <div className="text-center text-gray-500">
                <Shield className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="font-semibold">[Placeholder: Einsatzfahrzeug in {district.name}]</p>
                <p className="text-sm">Foto eines Servicefahrzeugs vor lokalen Wahrzeichen</p>
              </div>
            </div>

            <div className="bg-white border-2 border-[#0066cc] p-6 rounded-xl my-8">
              <h4 className="font-semibold text-gray-900 mb-3 text-lg">Schnelle Hilfe benötigt?</h4>
              <p className="text-gray-700 mb-4">
                Zögern Sie nicht, uns anzurufen. Unser Team berät Sie am Telefon kostenlos und 
                schickt schnellstmöglich einen Techniker zu Ihnen nach {district.name}.
              </p>
              <a href="tel:+4923139083687" className="inline-block" data-testid="district-cta-button">
                <Button size="lg" className="bg-[#0066cc] hover:bg-[#0052a3] text-white rounded-full font-bold">
                  <Phone className="w-5 h-5 mr-2" />
                  0231 39083687 – Jetzt anrufen
                </Button>
              </a>
            </div>

            <div className="mt-8">
              <Link
                to={`/einsatzgebiete/${district.citySlug}`}
                className="text-[#0066cc] hover:underline font-semibold"
                data-testid="back-to-city-link"
              >
                ← Zurück zu allen Stadtteilen in {district.city}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DistrictPage;